<template>
	<div>
		<div class="videoWrapper" :class="[{ loading }, { isFullscreen }]">
			<div v-if="loading" class="loading-icon">
				<div class="spinner">
					<font-awesome-icon icon="fa-solid fa-spinner" />
				</div>
			</div>

			<video
				ref="video"
				playsinline
				allowfullscreen="allow"
				preload="metadata"
				:class="{ loading }"
				:loop="loop"
				:muted="videoMuted"
				@loadedmetadata="onLoadedMetaData()"
				@timeupdate="onTimeUpdate()"
				@ended="onEnded()"
			>
				<source :src="src" type="video/mp4" />
			</video>

			<progress
				v-show="!loading"
				ref="progress"
				class="progress"
				:value="progressValue"
				min="0"
				max="100"
			/>
		</div>

		<div class="controls" :class="{ loading }">
			<button id="playpause" type="button" @click="togglePlay()">
				<font-awesome-icon v-if="isEnded" icon="fa-solid fa-arrow-rotate-left" />
				<font-awesome-icon v-else-if="!isPlaying" icon="fa-solid fa-play" />
				<font-awesome-icon v-else icon="fa-solid fa-pause" />
			</button>

			<div class="group">
				<button id="mute" type="button" @click="toggleMute()">
					<font-awesome-icon v-if="videoMuted" icon="fa-solid fa-volume-xmark" />
					<font-awesome-icon v-else icon="fa-solid fa-volume-high" />
				</button>
				<button id="fs" type="button" @click="enterFullScreen()">
					<font-awesome-icon icon="fa-solid fa-expand" />
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

export default {
	name: 'MediaVideo',

	props: {
		active: Boolean,
		src: String,
		autoplay: {
			default: false,
			type: Boolean
		},
		loop: {
			default: false,
			type: Boolean
		},
		forcePause: {
			default: false,
			type: Boolean
		}
	},

	data: () => ({
		duration: null,
		currentTime: 0,
		isPlaying: true,
		isFullscreen: false,
		isEnded: false,
		loading: true,
        hasMounted: false,
	}),

	watch: {
		active: {
			handler() {
				// make sure the dom is loaded and ready
				this.$nextTick(() => {
					this.active ? this.onActive() : this.pauseVideo()
				})
			},
			immediate: true
		},
		forcePause() {
			this.forcePause ? this.pauseVideo() : this.playVideo()
		}
	},

	computed: {
		...mapState({
			videoMutedPreference: (state) => state.videoMutedPreference
		}),

		progressValue() {
			return Math.floor((this.currentTime / this.duration) * 100)
		},

		videoMuted() {
			if (this.videoMutedPreference === null) {
				return true
			} else {
				return this.videoMutedPreference
			}
		}
	},

	mounted() {
		this.addEventListeners();
        this.hasMounted = true;

        if (this.active)
            this.onActive();
	},

	beforeDestroy() {
		this.pauseVideo()
		this.resetPlayback()
		this.removeEventListeners()
	},

	methods: {
		...mapMutations(['setVideoMutedPreference']),

		addEventListeners() {
			this.$refs.video.addEventListener('fullscreenchange', this.onFullScreenChange, false)
			this.$refs.video.addEventListener('webkitfullscreenchange', this.onFullScreenChange, false)
			this.$refs.video.addEventListener('mozfullscreenchange', this.onFullScreenChange, false)
			this.$refs.video.addEventListener('MSFullscreenChange', this.onFullScreenChange, false)
		},

		removeEventListeners() {
			this.$refs.video.removeEventListener('fullscreenchange', this.onFullScreenChange, false)
			this.$refs.video.removeEventListener('webkitfullscreenchange', this.onFullScreenChange, false)
			this.$refs.video.removeEventListener('mozfullscreenchange', this.onFullScreenChange, false)
			this.$refs.video.removeEventListener('MSFullscreenChange', this.onFullScreenChange, false)
		},

		// has become active and is the first card on the stack
		onActive(){
            if (!this.hasMounted)
                return;
            
			this.onCanplaythrough()
			this.resetPlayback()
		},

		onLoadedMetaData() {
			this.duration = this.$refs.video.duration
		},

		async onCanplaythrough() {
			// test if video can autoplay, if not mute it and play
			console.log('can play')
			try {
				await this.playVideo()
			} catch (err) {
				console.log(err)
				console.log('cant play so mute video')
				this.muteVideo()
				this.playVideo()
			}

			this.loading = false
		},

		onTimeUpdate() {
			this.currentTime = this.$refs.video.currentTime
		},

		onEnded() {
			this.isEnded = true
			this.isPlaying = false
		},

		async playVideo() {
			console.log('play video')
			try {
				await this.$refs.video.play()
			} catch (err) {
				console.log(err)
				console.log('cant play so mute video')
				this.muteVideo()
				await this.$refs.video?.play()
			}

			this.isPlaying = true
		},

		pauseVideo() {
			this.$refs.video.pause()
			this.isPlaying = false
		},

		resetPlayback() {
			this.$refs.video.currentTime = 0
			this.currentTime = 0
		},

		togglePlay() {
			this.isEnded = false
			this.isPlaying ? this.pauseVideo() : this.playVideo()
		},

		toggleMute() {
			this.$refs.video.muted = !this.$refs.video.muted
			this.setVideoMutedPreference(this.$refs.video.muted)
		},

		muteVideo() {
            
            if (!this?.$refs?.video)
                return;

            this.$refs.video.muted = true
			this.setVideoMutedPreference(true)
		},

		onFullScreenChange() {
			console.log(document.fullscreenElement)
			if (document.fullscreenElement || document.webkitIsFullScreen || document.mozFullScreen) {
				console.log('is fullscreen')
			} else {
				this.exitFullscreen()
				console.log('exit fullscreen')
			}
		},

		enterFullScreen() {
			if (this.$refs.video.requestFullscreen) this.$refs.video.requestFullscreen()
			else if (this.$refs.video.mozRequestFullScreen) this.$refs.video.mozRequestFullScreen()
			else if (this.$refs.video.webkitRequestFullscreen) this.$refs.video.webkitRequestFullscreen()
			else if (this.$refs.video.webkitEnterFullscreen) this.$refs.video.webkitEnterFullscreen()
			else if (this.$refs.video.msRequestFullscreen) this.$refs.video.msRequestFullscreen()

			this.isFullscreen = true
			console.log('enter fullscreen')
		},

		exitFullscreen() {
			if (this.$refs.video.exitFullscreen) this.$refs.video.exitFullscreen()
			else if (this.$refs.video.mozCancelFullScreen) this.$refs.video.mozCancelFullScreen()
			else if (this.$refs.video.webkitCancelFullScreen) this.$refs.video.webkitCancelFullScreen()
			else if (this.$refs.video.msExitFullscreen) this.$refs.video.msExitFullscreen()

			this.isFullscreen = false
			console.log('exit fullscreen')
		}
	}
}
</script>

<style lang="scss" scoped>
.videoWrapper {
	border-radius: $border-radius;
	overflow: hidden;
	position: relative;
	padding-bottom: 56.25%; /* 16:9 */
	height: 0;
	width: 100%;
	background: black;
	isolation: isolate;

	&.isFullscreen {
		isolation: auto;
	}
}

video {
	object-fit: contain;
	background: #000;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	backface-visibility: hidden;
	opacity: 1;
	transition: opacity 0.3s;

	&.loading {
		opacity: 0;
	}
}

progress {
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 7px;
	background: transparent;

	&::-webkit-progress-bar {
		background-color: transparent;
		width: 100%;
	}

	&[value] {
		appearance: none; /* Needed for Safari */
		border: none; /* Needed for Firefox */
		color: #e52e71; /* Fallback to a solid color */
		background: transparent;
		display: inline-block;

		&::-webkit-progress-value {
			background: $pink;
			border-radius: 4px;
		}

		&::-moz-progress-bar {
			background: $pink;
		}
	}
}

.controls {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 5px 10px 0 10px;
	opacity: 1;
	transition: opacity 0.3s;

	&.loading {
		opacity: 0;
	}
}

.controls button {
	border: none;
	cursor: pointer;
	background: transparent;
	background-size: contain;
	background-repeat: no-repeat;
	color: $pink;
	font-size: 18px;
	padding: 0;
	width: 32px;
	height: 32px;
	border-radius: 40px;
	display: inline-flex;
	align-items: center;
	justify-content: center;

	&:hover {
		background: $pinkOpacity;
	}

	& + button {
		margin-left: 5px;
	}
}

.loading-icon {
	color: white;
	height: 100%;
	width: 100%;
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		opacity: 0.5;
		font-size: 32px;
		animation-name: spin;
		animation-duration: 3000ms;
		animation-iteration-count: infinite;
		animation-timing-function: linear;
	}
}
</style>
